/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
    faAngleLeft,
    faAngleRight,
    faArrowRight,
    faBell,
    faPlus,
    faTemperatureLow
} from "@fortawesome/pro-regular-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Alert } from "reactstrap";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { CONFIG_TYPES } from "../../../../actions/Tenants/config/constants";
import { breakpoints } from "../../../../constants/mediaqueries";
import { isFilledArray } from "../../../../utils";
import { useRegisterDebugTargets } from "../../../../utils/useDebugTargets";
import { Button } from "../../../Helper/Button";
import GroupByModal from "../../../Helper/GroupByModal";
import { Loading } from "../../../Helper/Loading";
import SidebarPageLayout from "../../Layout/SidebarPageLayout";
import { SignalInboxModal } from "../../Signals";
import { ActiveSignalBar } from "../../Signals/components/ActiveSignalBar";
import { RoomSignalsTab } from "../../Signals/components/RoomSignalsTab";
import { SignalsBanner } from "../../Signals/components/SignalsBanner";
import { openCountsByCategory } from "../../Signals/signalFiltering";
import { useSignals } from "../../Signals/signalHooks";
import { Signal, SignalCategory } from "../../Signals/signalTypes";
import { useActiveSignal } from "../../Signals/useActiveSignal";
import RipeningRoomEvents from "../Helpers/RipeningRoomEvents";
import useRipeningRoom, {
    DATE_URL_FORMAT,
    LIST_OR_GRID,
    RipeningRoomProvider
} from "../hooks/useRipeningRoom";
import RipeningRoomBreadcrumbs from "../RipeningRoomBreadcrumbs";
import AddRipeningCycleNoteForm from "./AddRipeningCycleNoteForm";
import EditRipeningCycleForm from "./EditRipeningCycleForm";
import FruitSensorDataGraph from "./FruitSensorDataGraph";
import { RipeningRoomMetricMetaTable } from "./MetricCards";
import {
    AddSinglePalletModal,
    RemoveLayerModal,
    ScanToMoveLayerModal,
} from "./MovePalletsModal";
import { CheckSessionRipeningFieldGraphs, RipeningPalletsGraphs } from "./RipeningFieldGraphs";
import RipeningRoomBulkCheckButtons from "./RipeningRoomBulkCheckButtons";
import RipeningRoomDays from "./RipeningRoomDays";
import RipeningRoomItems from "./RipeningRoomItems";
import RipeningCellMetaTable from "./RipeningRoomMetaTable";
import RipeningRoomRipeningCycles from "./RipeningRoomRipeningCycles";
import RipeningRoomStatusCard from "./RipeningRoomStatusCard";
import RipeningRoomViewTabs from "./RipeningRoomViewTabs";
import SensorDataGraph from "./SensorDataGraph";
import { ScanToCheckModal } from "./StartCheckButton";
import StartRipeningCycleForm from "./StartRipeningCycleForm";

export default function RipeningRoomLayoutWrapped() {
    return (
        <RipeningRoomProvider>
            <RipeningRoomLayout />
        </RipeningRoomProvider>
    );
}

// * Below xl the sidebar stacks under the page content, out of sight: the status card then moves to the top of the content.
const SIDEBAR_BESIDE_QUERY = `(min-width: ${breakpoints[3]}px)`;

function useSidebarBeside() {
    const query = () => typeof window === "undefined" || typeof window.matchMedia !== "function" || window.matchMedia(SIDEBAR_BESIDE_QUERY).matches;
    const [beside, setBeside] = useState(query);
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
        const media = window.matchMedia(SIDEBAR_BESIDE_QUERY);
        const onChange = () => setBeside(media.matches);
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, []);
    return beside;
}

function RipeningRoomLayout() {
    const sidebarBeside = useSidebarBeside();
    const config = useConfig();
    const user = useSelector((state: any) => state.auth.user);
    const tenant_id = (user as any)?.tenant?.tenant_id;
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        listOrGrid,
        currentLocation,
        setListOrGrid,
        filterLevel,
        setFilterLevel,
        cellLevels,
        selectedLayersArray,
        refreshAllocatedLayers,
        deselectAll,
        pallets,
        groupByProps,
        roomEvents,
        today,
        allocatedLayersLoading,
        allocatedLayersError,
        fruit_type,
        alerts,
        ripeningCycles,
        prev,
        next,
        firstDayInRange,
        lastDayInRange,
        ripeningActionAllowed,
        activeRipeningCycle,
        viewingActiveCycle,
        setSearchParam,
    } = useRipeningRoom();

    const [addPalletModalOpen, setAddPalletModal] = useState(false);
    const [movePalletsModalOpen, setMovePalletsModal] = useState(false);
    const [activeView, setActiveView] = useState<"list" | "graph-pallets" | "grid" | "graph-check-session" | "graph-sensor-data" | "graph-fruit-sensor-data" | "signals">("list");

    // Room-scoped signals (R15) — feeds the summary banner, the tab badge, and the tab list.
    const [signalsCategory, setSignalsCategory] = useState<SignalCategory | null>(null);
    const signalsEnabled = !!config.root_config?.ripening_room_enable_signals;
    const cell = currentLocation?.location_id;
    const { data: roomSignalsData } = useSignals(
        { facility_location_id: cell, limit: 500 },
        { enabled: signalsEnabled && !!cell },
    );
    const roomSignals = roomSignalsData?.results ?? [];
    const openSignalCount = openCountsByCategory(roomSignals).total;

    // WP-4/D10: activating a signal from the room's own Signals tab never
    // navigates — every row there already carries this room's cell relation
    // — it just sets the param in place and returns the view to the
    // list/grid default so the operator lands on the evidence, not the list
    // the banner makes redundant. The list/grid sync effect below does NOT
    // do this for us: it only converts between "list" and "grid" and
    // deliberately leaves every other tab (including "signals") alone.
    // D13: `activeSignal` is only ever set once the hook's room-match guard
    // has settled — the same guard that makes a stale or foreign
    // `?activeSignal=` clear itself rather than show a bar. Passing its id
    // down (rather than reading the search param directly) is what keeps
    // the room's Signals tab from marking a row for a signal that guard
    // would otherwise have suppressed.
    const { activeSignal, setActiveSignal } = useActiveSignal(currentLocation?.location_id);
    const handleRoomTabActivate = (signal: Signal) => {
        setActiveSignal(signal.id);
        setActiveView(listOrGrid === LIST_OR_GRID.GRID ? "grid" : "list");
    };

    // Keep activeView in step with the list/grid toggle. "grid" is the only
    // view bound to GRID mode, so it is the only one to force back to "list"
    // when the toggle flips to LIST — every other tab (graphs, signals) is
    // valid in either mode and must be left alone, or clicking it snaps back.
    useEffect(() => {
        if (listOrGrid === LIST_OR_GRID.LIST && activeView === "grid") {
            setActiveView("list");
        } else if (listOrGrid === LIST_OR_GRID.GRID && activeView === "list") {
            setActiveView("grid");
        }
    }, [listOrGrid, activeView]);

    const roomDebugData = useMemo(() => ({
        location: currentLocation,
        listOrGrid,
        cellLevels,
        selectedLayers: selectedLayersArray,
        pallets,
        groupBy: groupByProps,
        events: roomEvents,
        cycles: ripeningCycles,
        activeRipeningCycle,
        filters: { filterLevel },
        timeframe: { firstDayInRange, lastDayInRange },
        loading: { allocatedLayersLoading, allocatedLayersError },
        ripeningActionAllowed,
        viewingActiveCycle,
    }), [
        currentLocation,
        listOrGrid,
        cellLevels,
        selectedLayersArray,
        pallets,
        groupByProps,
        roomEvents,
        ripeningCycles,
        activeRipeningCycle,
        filterLevel,
        firstDayInRange,
        lastDayInRange,
        allocatedLayersLoading,
        allocatedLayersError,
        ripeningActionAllowed,
        viewingActiveCycle,
    ]);

    // * Register ripening room debug targets: hook state inspection + shortcuts to config pages
    useRegisterDebugTargets("ripening_room_state", tenant_id ? [{
        id: `ripening_room_state_${currentLocation?.id}`,
        url: "#",
        text: "Inspect room state",
        debug: {
            props: { data: roomDebugData },
            title: `Room State: ${currentLocation?.label || "Loading..."}`,
        },
    }] : []);

    // * "Root" is a singleton config (value === "root"), so there is always at most one.
    const root_config_id = config.configs?.find((c) => c.type === CONFIG_TYPES.ROOT && c.value === "root")?.id;

    useRegisterDebugTargets("ripening_room_shortcuts", tenant_id && root_config_id ? [
        {
            id: `ripening_room_config_${tenant_id}`,
            url: `/tenants/${tenant_id}/config/${root_config_id}`,
            text: "Room config (Root)",
        },
        {
            id: `ripening_room_locations_${tenant_id}`,
            url: `/tenants/${tenant_id}/facility-locations`,
            text: "List of rooms",
        },
    ] : []);

    const openConditionsModal = () => {
        setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set("openConditionsModal", today.format(DATE_URL_FORMAT));
            return newSearchParams;
        });
    };

    const openSignalsInbox = () => {
        setSearchParams((prev) => {
            const newSearchParams = new URLSearchParams(prev);
            newSearchParams.set("signals", "inbox");
            return newSearchParams;
        });
    };

    const disableAllocationUI = config.root_config?.ripening_room_disable_allocation_ui;

    const bottomBarLeft = useMemo(() => {
        return (
            <>
                <GroupByModal
                    title="Group pallets"
                    description="Group pallets in this room by one of these fields"
                    groupByProps={groupByProps}
                    isInBottomBar
                />
                {ripeningActionAllowed && <Button
                    disabled={selectedLayersArray.length === 0}
                    color={selectedLayersArray.length === 0 ? "light" : "primary"}
                    onClick={() => setMovePalletsModal(true)}
                    icon={faArrowRight}
                    isInBottomBar
                >
                    Move {selectedLayersArray.length}
                </Button>}
                {ripeningActionAllowed && <RipeningRoomBulkCheckButtons />}
                {ripeningActionAllowed && !disableAllocationUI && <ScanToMoveLayerModal isInBottomBar />}
                {ripeningActionAllowed && !disableAllocationUI && <Button
                    color="light"
                    onClick={() => setAddPalletModal(true)}
                    icon={faPlus}
                    isInBottomBar
                >
                    Add pallets
                </Button>}


            </>
        );
    }, [config, selectedLayersArray, groupByProps, listOrGrid, filterLevel, disableAllocationUI]);

    const bottomBarRight = (
        <>
            <StartRipeningCycleForm isInBottomBar />
            <AddRipeningCycleNoteForm isInBottomBar />
            <EditRipeningCycleForm isInBottomBar />
            { activeRipeningCycle && !viewingActiveCycle && (
                <Button
                    color="light"
                    icon={faAngleLeft}
                    onClick={() => setSearchParam("ripening_cycle_id", activeRipeningCycle.id)}
                    isInBottomBar
                >
                    Current cycle
                </Button>
            )}
            {ripeningActionAllowed && currentLocation?.client_room && <Button color="primary" icon={faTemperatureLow} onClick={openConditionsModal} isInBottomBar>Conditions</Button>}

            {ripeningActionAllowed && activeRipeningCycle && <ScanToCheckModal isInBottomBar />}


            {ripeningActionAllowed && signalsEnabled && <Button
                color="primary"
                onClick={openSignalsInbox}
                icon={faBell}
                isInBottomBar
            >
                Signals
            </Button>}
        </>
    );

    if (!currentLocation) {
        return <Loading />;
    }

    const roomIsNotEmpty = pallets?.length > 0;
    const hasFruitTypeConfig = !!fruit_type && !!config.fruit_types?.some((ft: any) => ft?.value === fruit_type);
    const showFruitTypeConfigWarning = roomIsNotEmpty && config.configs_loaded && (!fruit_type || !hasFruitTypeConfig);

    const getChildComponent = () => {
        if (allocatedLayersError) {
            return (
                <div css={css`min-height: 60vh;`} className="d-flex align-items-center justify-content-center">
                    <div className="text-center text-danger">
                        <div className="mb-2">Error loading allocated pallets</div>
                        <div className="small">Please try refreshing the page</div>
                    </div>
                </div>
            );
        }

        if (allocatedLayersLoading && !roomIsNotEmpty) {
            return <Loading />;
        }

        if (!allocatedLayersLoading && !roomIsNotEmpty) {
            // * A room linked to a gateway still has sensor data without pallets in it.
            const hasSensorData = !!currentLocation?.gateway && !!currentLocation?.controller_room;
            return (
                <div className="d-flex flex-column gap-5">
                    <div css={css`min-height: ${hasSensorData ? "20vh" : "60vh"};`} className="d-flex align-items-center justify-content-center">
                        <div className="text-center text-muted">
                            <h3><em>Empty Room</em></h3>
                            <div className="small">Allocate pallets to get started or navigate to another timeframe!</div>
                            <div className="d-flex gap-2 justify-content-center py-5 align-items-center">
                                <Button color="primary" icon={faAngleLeft} onClick={prev} />
                                <div className="px-3">From <b>{firstDayInRange.format("ddd D MMM")}</b> untill <b>{lastDayInRange.format("ddd D MMM")}</b></div>
                                <Button color="primary" icon={faAngleRight} onClick={next} />
                            </div>
                        </div>
                    </div>
                    {hasSensorData && (
                        <div className="bg-white py-5 px-5 py-md-3 px-md-3">
                            <SensorDataGraph />
                        </div>
                    )}
                </div>
            );
        }

        if (roomIsNotEmpty) {
            return (
                <div className="d-flex flex-column gap-5">
                    <RipeningRoomDays />
                    <div>
                        <RipeningRoomViewTabs
                            setListOrGrid={setListOrGrid}
                            filterLevel={filterLevel || ""}
                            setFilterLevel={setFilterLevel}
                            cellLevels={cellLevels}
                            showGridOptions={config.root_config?.ripening_room_slot_allocation_from_erp}
                            activeView={activeView}
                            onViewChange={setActiveView}
                            enableFruitSensorGraph={config.root_config?.ripening_room_enable_fruit_sensor_graph}
                            signalsEnabled={signalsEnabled}
                            openSignalCount={openSignalCount}
                        />

                        <div css={css`min-height: 60vh;`} className="bg-white py-5 px-5 py-md-3 px-md-3">
                            <div>
                                {activeView === "list" && <RipeningRoomItems />}
                                {activeView === "grid" && <RipeningRoomItems />}
                                {activeView === "graph-check-session" && (
                                    <CheckSessionRipeningFieldGraphs />
                                )}
                                {activeView === "graph-pallets" && (
                                    <RipeningPalletsGraphs />
                                )}
                                {activeView === "graph-sensor-data" && (
                                    <SensorDataGraph />
                                )}
                                {activeView === "graph-fruit-sensor-data" && config.root_config?.ripening_room_enable_fruit_sensor_graph && (
                                    <FruitSensorDataGraph />
                                )}
                                {activeView === "signals" && signalsEnabled && (
                                    <RoomSignalsTab signals={roomSignals} initialCategory={signalsCategory} onActivate={handleRoomTabActivate} activeSignalId={activeSignal?.id} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <>
            {/* The active-signal bar pins itself to the top of the viewport, so it
                mounts OUTSIDE SidebarPageLayout's content flow — a CSS transform on
                any ancestor would make its `position: fixed` resolve against that
                ancestor instead of the viewport. It stays inside
                RipeningRoomProvider so it can read the room being viewed. */}
            {signalsEnabled && <ActiveSignalBar />}

            <SidebarPageLayout
                styleAsCard={false}
                title={<div className="d-flex w-100 justify-content-between">
                    <h3>{currentLocation?.label}</h3>
                    {config.root_config?.ripening_room_enable_signals && (
                        <Button color="secondary" outline icon={faBell} onClick={openSignalsInbox}>Signals</Button>
                    )}
                </div>}
                breadcrumbs={<RipeningRoomBreadcrumbs currentLocation={currentLocation} />}
                sidebar={
                    <>
                        {sidebarBeside && (
                            <div className="mb-4">
                                <RipeningRoomStatusCard />
                            </div>
                        )}

                        <RipeningRoomMetricMetaTable />

                        {isFilledArray(ripeningCycles) && (
                            <div className="mt-5 pt-5 border-top">
                                <RipeningRoomRipeningCycles />
                            </div>
                        )}

                        {isFilledArray(config.root_config?.ripening_room_sidebar_meta_fields_1) && (
                            <div className="mt-5 pt-5 border-top">
                                <RipeningCellMetaTable fields={config.root_config.ripening_room_sidebar_meta_fields_1} />
                            </div>
                        )}

                        {isFilledArray(config.root_config?.ripening_room_sidebar_meta_fields_2) && (
                            <div className="mt-5 pt-5 border-top">
                                <RipeningCellMetaTable fields={config.root_config.ripening_room_sidebar_meta_fields_2} />
                            </div>
                        )}
                        {isFilledArray(roomEvents) && (
                            <div className="mt-5 pt-5 border-top">
                                <RipeningRoomEvents events={roomEvents} />
                            </div>
                        )}
                    </>
                }
                bottomBar={
                    <div className="d-flex flex-wrap w-100 align-items-center justify-content-center justify-content-md-between gap-4 gap-md-2">
                        <div className="d-flex flex-wrap align-items-center justify-content-center gap-4 gap-md-2">
                            {bottomBarLeft}
                        </div>
                        <div className="d-flex flex-wrap align-items-center justify-content-center gap-4 gap-md-2">
                            {bottomBarRight}
                        </div>
                    </div>
                }
            >
                {!sidebarBeside && (
                    <div className="mb-3">
                        <RipeningRoomStatusCard />
                    </div>
                )}

                {/* Display collected alerts */}
                {alerts.map((alert) => (
                    <Alert
                        key={alert.id}
                        color={alert.color}
                        className="mb-3"
                    >
                        {alert.message}
                    </Alert>
                ))}

                {showFruitTypeConfigWarning && (
                    <Alert color="warning" className="mb-3">
                        {fruit_type
                            ? `No ripening classes config was found for fruit type "${fruit_type}". Some fruit-type based fields may be hidden.`
                            : "Allocated pallets are missing fruit type data. Some fruit-type based fields may be hidden until fruit_type is available."}
                    </Alert>
                )}

                <div
                    className="d-flex flex-wrap align-items-center gap-2 mb-3"
                    css={css`&:empty { display: none; }`}
                >
                    {signalsEnabled && (
                        <SignalsBanner
                            className=""
                            signals={roomSignals}
                            onCategoryClick={(category) => {
                                setSignalsCategory(category);
                                setActiveView("signals");
                            }}
                        />
                    )}
                </div>

                {getChildComponent()}


                <AddSinglePalletModal
                    setModal={setAddPalletModal}
                    modalOpen={addPalletModalOpen}
                />

                <RemoveLayerModal
                    setModal={setMovePalletsModal}
                    modalOpen={movePalletsModalOpen}
                    selectedLayers={selectedLayersArray}
                    refreshAllocatedLayers={refreshAllocatedLayers}
                    deselectAll={deselectAll}
                />

                {signalsEnabled && <SignalInboxModal isOpen={searchParams.get("signals") === "inbox"} />}
            </SidebarPageLayout>
        </>
    );
}
